// QrSheet — iter110.
//
// Reusable bottom sheet that displays a profile QR (either the signed-in
// user's own QR or a friend's). Used from the Friends page bottom-bar
// "My QR" button and from each friend card's "Show QR Code" CTA.
//
// Layout:
//   • Handle bar at top.
//   • 44×44 X close button (32px dark visual circle).
//   • Playfair 18px title — "My QR Code" / "{Name}'s QR".
//   • 56×56 round avatar (profile_photo OR initials).
//   • DM Sans 16px name + Tourist/Local pill underneath.
//   • 200px QR rendered on opaque white.
//   • DM Sans 13px caption.
//   • Two equal action buttons (gap 8px, 48px tall):
//       – "Share Link" — green WhatsApp outline; opens
//         https://wa.me/?text=<URL> in a new tab.
//       – "Save QR"    — gold outline; downloads the rendered QR as a
//         PNG named <name>-habano-qr.png.
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useI18n } from "../lib/i18n";
import { SHOW_VIEW_TOGGLE } from "../lib/featureFlags";

export default function QrSheet({
  open, onClose,
  kind = "my",           // "my" | "friend"
  displayName,
  profilePhoto,
  viewPreference,        // "tourist" | "local" | null
  qrUrl,
}) {
  const { t } = useI18n();
  const qrWrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const name = displayName || "Habano";
  const title = kind === "my"
    ? t("friends_qr_title_my")
    : t("friends_qr_title_friend").replace("{name}", name);
  const caption = kind === "my"
    ? t("friends_qr_caption_my")
    : t("friends_qr_caption_friend").replace("{name}", name);

  const onShareLink = () => {
    const text = `${t("friends_qr_share_text")}: ${qrUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  const onSaveQr = () => {
    const svg = qrWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    const size = 720;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#F5F1E8"; ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 40, 40, size - 80, size - 80);
      const safeName = (name || "habano").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const link = document.createElement("a");
      link.download = `${safeName}-habano-qr.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  const sheet = (
    <div
      data-testid={`qr-sheet-${kind}`}
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 220, display: "flex", alignItems: "flex-end" }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="qr-sheet-scrim"
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", border: "none", padding: 0 }}
      />
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          background: "#171717",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          borderTop: "1px solid rgba(232,184,75,0.3)",
          padding: "10px 20px 24px",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.5)",
        }}
      >
        <div
          aria-hidden="true"
          style={{ width: 40, height: 4, background: "rgba(232,184,75,0.45)", borderRadius: 9999, margin: "4px auto 8px" }}
        />

        <button
          type="button"
          onClick={onClose}
          data-testid="qr-sheet-close"
          aria-label="Close"
          className="absolute grid place-items-center hover:opacity-80 transition-opacity"
          style={{
            top: 6, right: 6, width: 44, height: 44, minWidth: 44, minHeight: 44,
            background: "transparent", border: "none", padding: 0,
          }}
        >
          <span
            aria-hidden="true"
            className="grid place-items-center"
            style={{
              width: 32, height: 32, borderRadius: 9999,
              background: "rgba(20,20,20,0.8)",
              border: "0.5px solid #333", color: "#fff",
            }}
          >
            <X style={{ width: 16, height: 16 }} strokeWidth={1.5} />
          </span>
        </button>

        <h2
          data-testid="qr-sheet-title"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 18,
            color: "#F5F1E8",
            lineHeight: 1.25,
            marginRight: 48,
            marginBottom: 14,
          }}
        >
          {title}
        </h2>

        <div className="flex flex-col items-center">
          <Avatar name={name} photo={profilePhoto} />
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 600,
              color: "#F5F1E8",
              marginTop: 10,
              textAlign: "center",
            }}
            data-testid="qr-sheet-name"
          >
            {name}
          </div>
          <ViewBadge view={viewPreference} t={t} />

          <div
            ref={qrWrapRef}
            data-testid="qr-sheet-code"
            style={{
              marginTop: 16,
              background: "#F5F1E8",
              padding: 16,
              borderRadius: 14,
            }}
          >
            <QRCodeSVG
              value={qrUrl || ""}
              size={200}
              level="M"
              fgColor="#0B0B0B"
              bgColor="#F5F1E8"
            />
          </div>

          <p
            data-testid="qr-sheet-caption"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#9A958A",
              textAlign: "center",
              marginTop: 14,
              marginBottom: 16,
              maxWidth: 320,
              lineHeight: 1.45,
            }}
          >
            {caption}
          </p>

          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <button
              type="button"
              data-testid="qr-sheet-share-link"
              onClick={onShareLink}
              className="inline-flex items-center justify-center gap-2 transition-colors hover:bg-[rgba(37,211,102,0.06)]"
              style={{
                flex: 1, height: 48, minHeight: 48,
                background: "transparent",
                color: "#25D366",
                border: "1.5px solid rgba(37,211,102,0.55)",
                borderRadius: 12,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 600, padding: "0 12px",
              }}
            >
              <WaIcon size={18} />
              <span>{t("friends_qr_share_link")}</span>
            </button>
            <button
              type="button"
              data-testid="qr-sheet-save"
              onClick={onSaveQr}
              className="inline-flex items-center justify-center gap-2 transition-colors hover:bg-[rgba(232,184,75,0.06)]"
              style={{
                flex: 1, height: 48, minHeight: 48,
                background: "transparent",
                color: "#E8B84B",
                border: "1.5px solid rgba(232,184,75,0.55)",
                borderRadius: 12,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 600, padding: "0 12px",
              }}
            >
              <QrCode style={{ width: 18, height: 18 }} strokeWidth={2} />
              <span>{t("friends_qr_save")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

function Avatar({ name, photo }) {
  if (photo) {
    return (
      <img
        src={photo} alt="" data-testid="qr-sheet-avatar-img"
        style={{
          width: 56, height: 56, borderRadius: 9999, objectFit: "cover",
          border: "1px solid rgba(232,184,75,0.4)",
        }}
      />
    );
  }
  const initials = (name || "?").split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      data-testid="qr-sheet-avatar-initials"
      className="grid place-items-center"
      style={{
        width: 56, height: 56, borderRadius: 9999,
        background: "#0B0B0B", color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
        border: "1px solid rgba(232,184,75,0.4)",
      }}
    >
      {initials}
    </span>
  );
}

function ViewBadge({ view, t }) {
  // iter156 — Hidden globally via SHOW_VIEW_TOGGLE. Component + logic
  // are preserved so flipping the flag restores the pill.
  if (!SHOW_VIEW_TOGGLE) return null;
  if (view !== "tourist" && view !== "local") return null;
  const label = view === "tourist" ? t("friends_badge_tourist") : t("friends_badge_local");
  return (
    <span
      data-testid={`qr-sheet-badge-${view}`}
      style={{
        marginTop: 6,
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        background: "rgba(232,184,75,0.08)",
        border: "1px solid rgba(232,184,75,0.4)",
        color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  );
}

function WaIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24z" />
    </svg>
  );
}
